import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import { createMapSourceAtom } from './atoms';
import type { MapUserConfigAtom } from './types';

/** Default fill + outline for the portfolio tract polygons (IBM Yellow 30).
 *  Fill and outline share one color; the outline reads as a crisper edge only
 *  because it's drawn at a higher opacity than the translucent fill. */
const DEFAULT_FILL_COLOR = '#F1C21B';
const DEFAULT_LINE_COLOR = DEFAULT_FILL_COLOR;

/**
 * MVT tile layer for Portfolio Assets — assets belonging to entities flagged
 * as portfolio investments. Renders yellow polygons, gated on
 * `mapConfig.portfolioAssets.checked`.
 * @param {object} props - Component props.
 * @param {MapUserConfigAtom} props.mapConfigAtom - The map config atom.
 * @param {string} [props.fillColor] - Polygon fill color (defaults to yellow).
 * @param {string} [props.lineColor] - Polygon outline color (defaults to the
 *   same yellow as the fill). Callers that let a user recolor the layer
 *   typically pass the same value for both.
 * @param {boolean} [props.visible] - Overrides visibility. When omitted the
 *   layer falls back to `mapConfig.portfolioAssets.checked`; callers that drive
 *   visibility from their own state (e.g. a dataset toggle) pass it explicitly.
 * @return {JSX.Element | null} The source + layers, or null when the tile
 *   source hasn't loaded yet.
 */
export const PortfolioAssetsLayer = ({
  mapConfigAtom,
  fillColor = DEFAULT_FILL_COLOR,
  lineColor = DEFAULT_LINE_COLOR,
  visible: visibleOverride,
}: {
  mapConfigAtom: MapUserConfigAtom;
  fillColor?: string;
  lineColor?: string;
  visible?: boolean;
}) => {
  const sourceAtom = useMemo(() => createMapSourceAtom('portfolioAssets'), []);
  const source = useAtomValue(sourceAtom);
  const mapConfig = useAtomValue(mapConfigAtom);

  if (!source) return null;

  const visible =
    visibleOverride ?? mapConfig?.portfolioAssets?.checked ?? false;

  return (
    <LayerBand band={LAYER_ORDER.portfolioAssets}>
      <Source id="portfolio-assets" type="vector" url={source}>
        {visible && (
          <Layer
            id="portfolio-assets-fill"
            type="fill"
            source-layer="portfolio-assets"
            minzoom={5}
            paint={{
              'fill-color': fillColor,
              'fill-opacity': 0.45,
            }}
          />
        )}
        {visible && (
          <Layer
            id="portfolio-assets-outline"
            type="line"
            source-layer="portfolio-assets"
            minzoom={5}
            paint={{
              'line-color': lineColor,
              'line-width': 1,
              'line-opacity': 0.9,
            }}
          />
        )}
      </Source>
    </LayerBand>
  );
};
